<script setup>
import { computed, ref, shallowRef, markRaw, watch, onMounted, onUnmounted, nextTick } from "vue";
import {
    mdiClose,
    mdiDownload,
    mdiPrinter,
    mdiFilePdfBox,
    mdiImage,
    mdiFileDocumentOutline,
    mdiFullscreen,
    mdiFullscreenExit,
    mdiWindowMaximize,
    mdiWindowRestore,
    mdiMagnifyPlusOutline,
    mdiMagnifyMinusOutline,
    mdiRefresh,
    mdiLoading
} from "@mdi/js";
import BaseIcon from "@/Components/BaseIcon.vue";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    file: {
        type: [Object, String],
        default: null,
    },
    title: {
        type: String,
        default: "",
    },
    onlyread: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["update:modelValue", "close"]);

const isOpen = computed({
    get: () => props.modelValue,
    set: (val) => emit("update:modelValue", val),
});

const isMaximized = ref(false);
const isFullscreen = ref(false);
const modalBoxRef = ref(null);
const scrollContainerRef = ref(null);

// Estado de PDF.js (shallowRef evita que Vue 3 envuelva PDF.js en Proxy que rompe campos privados)
const isPdfLoading = ref(false);
const pdfDoc = shallowRef(null);
const totalPages = ref(0);
const pdfScale = ref(1.2);
const pdfCanvasRefs = ref([]);
const pdfJsAvailable = ref(false);
const pdfLoadError = ref(false);

// Bloquear scroll de la página de fondo cuando el modal está abierto
watch(isOpen, (val) => {
    if (val) {
        document.body.style.overflow = "hidden";
        if (isPdf.value) {
            initPdfViewer();
        }
    } else {
        document.body.style.overflow = "";
        pdfDoc.value = null;
        totalPages.value = 0;
    }
}, { immediate: true });

const toggleMaximize = () => {
    isMaximized.value = !isMaximized.value;
};

const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
        if (modalBoxRef.value && modalBoxRef.value.requestFullscreen) {
            modalBoxRef.value.requestFullscreen();
            isFullscreen.value = true;
        }
    } else {
        if (document.exitFullscreen) {
            document.exitFullscreen();
            isFullscreen.value = false;
        }
    }
};

const handleFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement;
};

const closeModal = () => {
    if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => {});
    }
    isMaximized.value = false;
    isFullscreen.value = false;
    isOpen.value = false;
    emit("close");
};

// URL y metadatos del archivo
const fileUrl = computed(() => {
    if (!props.file) return "";
    if (typeof props.file === "string") return props.file;
    if (props.file.url) return props.file.url;
    if (props.file.filepath && props.file.filename) {
        let fp = props.file.filepath.replace(/^\/+|\/+$/g, '');
        return `/documents/${fp}/${props.file.filename}`;
    }
    if (props.file.filename) {
        return `/documents/${props.file.filename}`;
    }
    return "";
});

const fileName = computed(() => {
    if (props.title) return props.title;
    if (!props.file) return "Vista previa de documento";
    if (typeof props.file === "string") {
        return props.file.split("/").pop() || "Documento";
    }
    return props.file.name || props.file.filename || "Documento";
});

const fileExtension = computed(() => {
    const raw = typeof props.file === "string" ? props.file : (props.file?.filename || props.file?.name || "");
    const ext = raw.split(".").pop()?.toLowerCase() || "";
    return ext;
});

const isImage = computed(() => {
    return ["jpg", "jpeg", "png", "webp", "gif", "svg", "bmp"].includes(fileExtension.value);
});

const isPdf = computed(() => {
    return fileExtension.value === "pdf";
});

const isOffice = computed(() => {
    return ["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(fileExtension.value);
});

// Cargar script de PDF.js si no está presente
const loadPdfJsLibrary = () => {
    return new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
            resolve(window.pdfjsLib);
            return;
        }
        const existingScript = document.getElementById("pdfjs-cdn-script");
        if (existingScript) {
            existingScript.addEventListener("load", () => resolve(window.pdfjsLib));
            return;
        }
        const script = document.createElement("script");
        script.id = "pdfjs-cdn-script";
        script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
        script.onload = () => {
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
            resolve(window.pdfjsLib);
        };
        script.onerror = (err) => reject(err);
        document.head.appendChild(script);
    });
};

const renderPage = async (pdf, pageNum, canvas) => {
    if (!canvas) return;
    try {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: pdfScale.value });
        const context = canvas.getContext("2d");
        
        const outputScale = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = Math.floor(viewport.width) + "px";
        canvas.style.height = Math.floor(viewport.height) + "px";

        const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

        const renderContext = {
            canvasContext: context,
            transform: transform,
            viewport: viewport,
        };

        await page.render(renderContext).promise;
    } catch (err) {
        console.warn(`Error al renderizar página ${pageNum}:`, err);
    }
};

const renderAllPages = async () => {
    if (!pdfDoc.value) return;
    await nextTick();
    for (let pageNum = 1; pageNum <= totalPages.value; pageNum++) {
        const canvas = pdfCanvasRefs.value[pageNum - 1];
        if (canvas) {
            await renderPage(pdfDoc.value, pageNum, canvas);
        }
    }
};

const initPdfViewer = async () => {
    if (!fileUrl.value || !isPdf.value) return;
    isPdfLoading.value = true;
    pdfLoadError.value = false;

    try {
        const pdfjs = await loadPdfJsLibrary();
        pdfJsAvailable.value = true;

        const loadingTask = pdfjs.getDocument({
            url: fileUrl.value,
            cMapUrl: "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/",
            cMapPacked: true,
        });

        const doc = await loadingTask.promise;
        pdfDoc.value = markRaw(doc);
        totalPages.value = doc.numPages;
        pdfCanvasRefs.value = [];
        
        await nextTick();
        await renderAllPages();
    } catch (err) {
        console.warn("Fallo al inicializar visor PDF.js Canvas, usando fallback:", err);
        pdfLoadError.value = true;
        pdfJsAvailable.value = false;
    } finally {
        isPdfLoading.value = false;
    }
};

// Controles de zoom para PDF
const zoomIn = () => {
    if (pdfScale.value < 3.0) {
        pdfScale.value = parseFloat((pdfScale.value + 0.2).toFixed(2));
        renderAllPages();
    }
};

const zoomOut = () => {
    if (pdfScale.value > 0.6) {
        pdfScale.value = parseFloat((pdfScale.value - 0.2).toFixed(2));
        renderAllPages();
    }
};

const zoomReset = () => {
    pdfScale.value = 1.2;
    renderAllPages();
};

// Bloqueo de atajos de descarga/impresión cuando onlyread es true
const handleKeyDown = (e) => {
    if (e.key === "Escape" && isOpen.value) {
        closeModal();
        return;
    }
    if (props.onlyread && isOpen.value) {
        if ((e.ctrlKey || e.metaKey) && ["p", "s", "u", "S", "P", "U"].includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        }
        if (e.key === "F12" || ((e.ctrlKey || e.metaKey) && e.shiftKey && ["I", "i", "J", "j", "C", "c"].includes(e.key))) {
            e.preventDefault();
            e.stopPropagation();
            return false;
        }
    }
};

watch(() => props.file, () => {
    if (isOpen.value && isPdf.value) {
        initPdfViewer();
    }
});

onMounted(() => {
    window.addEventListener("keydown", handleKeyDown);
    document.addEventListener("fullscreenchange", handleFullscreenChange);
});

onUnmounted(() => {
    document.body.style.overflow = "";
    window.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("fullscreenchange", handleFullscreenChange);
});
</script>

<template>
    <Teleport to="body">
        <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
        >
            <div
                v-if="isOpen"
                class="fixed inset-0 z-[9999] flex items-center justify-center p-0 sm:p-3 md:p-6 bg-black/80 backdrop-blur-sm overflow-hidden"
                :class="{ 'select-none': onlyread }"
                @click.self="closeModal"
                @contextmenu.prevent
            >
                <div
                    ref="modalBoxRef"
                    class="bg-white dark:bg-slate-900 shadow-2xl border border-gray-200 dark:border-slate-800 flex flex-col overflow-hidden transition-all duration-200"
                    :class="[
                        isMaximized || isFullscreen
                            ? 'w-full h-full max-w-none max-h-none rounded-none'
                            : 'w-full max-w-6xl h-[94vh] rounded-2xl'
                    ]"
                    @click.stop
                    @contextmenu.prevent
                >
                    <!-- Header -->
                    <div class="px-5 py-3 border-b border-gray-200 dark:border-slate-800 flex items-center justify-between bg-gray-50/95 dark:bg-slate-800/95 backdrop-blur-md shrink-0">
                        <div class="flex items-center gap-3 min-w-0 pr-4">
                            <div class="p-2 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                                <BaseIcon v-if="isPdf" :path="mdiFilePdfBox" :size="22" class="text-red-500" />
                                <BaseIcon v-else-if="isImage" :path="mdiImage" :size="22" class="text-emerald-500" />
                                <BaseIcon v-else :path="mdiFileDocumentOutline" :size="22" class="text-blue-500" />
                            </div>
                            <div class="truncate">
                                <h3 class="font-bold text-gray-800 dark:text-gray-100 text-sm sm:text-base truncate">
                                    {{ fileName }}
                                </h3>
                                <div class="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                    <span v-if="fileExtension" class="uppercase font-semibold px-1.5 py-0.5 rounded bg-gray-200 dark:bg-slate-700 text-[10px]">
                                        {{ fileExtension }}
                                    </span>
                                    <span v-if="onlyread" class="text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
                                        <i class="fa-solid fa-lock text-[10px]"></i> Modo solo lectura
                                    </span>
                                    <span v-if="isPdf && totalPages > 0" class="text-gray-500 font-medium hidden sm:inline">
                                        • {{ totalPages }} {{ totalPages === 1 ? 'página' : 'páginas' }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Botones de Acción (Zoom, Maximizar, Fullscreen, Descargar, Cerrar) -->
                        <div class="flex items-center gap-1.5 shrink-0">
                            <!-- Controles de Zoom para PDF -->
                            <div v-if="isPdf && pdfDoc" class="flex items-center gap-1 mr-2 px-2 py-1 bg-gray-100 dark:bg-slate-800 rounded-xl">
                                <button
                                    type="button"
                                    @click="zoomOut"
                                    class="p-1 text-gray-600 dark:text-gray-300 hover:text-blue-600 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition"
                                    title="Alejar (-)"
                                >
                                    <BaseIcon :path="mdiMagnifyMinusOutline" :size="17" />
                                </button>
                                <span class="text-xs font-semibold px-1 min-w-[38px] text-center text-gray-700 dark:text-gray-300">
                                    {{ Math.round(pdfScale * 100) }}%
                                </span>
                                <button
                                    type="button"
                                    @click="zoomIn"
                                    class="p-1 text-gray-600 dark:text-gray-300 hover:text-blue-600 hover:bg-white dark:hover:bg-slate-700 rounded-lg transition"
                                    title="Acercar (+)"
                                >
                                    <BaseIcon :path="mdiMagnifyPlusOutline" :size="17" />
                                </button>
                                <button
                                    type="button"
                                    @click="zoomReset"
                                    class="p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-lg transition ml-0.5"
                                    title="Restablecer zoom"
                                >
                                    <BaseIcon :path="mdiRefresh" :size="15" />
                                </button>
                            </div>

                            <!-- Botón Descargar (solo si no es onlyread) -->
                            <a
                                v-if="!onlyread && fileUrl"
                                :href="fileUrl"
                                target="_blank"
                                download
                                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-100 dark:hover:bg-slate-600 border border-gray-300 dark:border-slate-600 rounded-lg transition-colors shadow-sm"
                                title="Descargar archivo"
                            >
                                <BaseIcon :path="mdiDownload" :size="16" />
                                <span class="hidden sm:inline">Descargar</span>
                            </a>

                            <!-- Botón Maximizar / Restaurar -->
                            <button
                                type="button"
                                @click="toggleMaximize"
                                class="p-1.5 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                :title="isMaximized ? 'Restaurar tamaño' : 'Maximizar ventana'"
                            >
                                <BaseIcon :path="isMaximized ? mdiWindowRestore : mdiWindowMaximize" :size="18" />
                            </button>

                            <!-- Botón Pantalla Completa -->
                            <button
                                type="button"
                                @click="toggleFullscreen"
                                class="p-1.5 text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
                                :title="isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'"
                            >
                                <BaseIcon :path="isFullscreen ? mdiFullscreenExit : mdiFullscreen" :size="18" />
                            </button>

                            <!-- Botón Cerrar -->
                            <button
                                type="button"
                                @click="closeModal"
                                class="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition-colors ml-1"
                                title="Cerrar (Esc)"
                            >
                                <BaseIcon :path="mdiClose" :size="20" />
                            </button>
                        </div>
                    </div>

                    <!-- Contenido del Visor -->
                    <div 
                        ref="scrollContainerRef"
                        class="flex-1 overflow-y-auto overflow-x-auto p-4 flex flex-col items-center bg-gray-200/80 dark:bg-slate-950 relative custom-viewer-scrollbar select-none"
                        @contextmenu.prevent
                    >
                        <!-- Loader PDF -->
                        <div v-if="isPdf && isPdfLoading" class="flex flex-col items-center justify-center h-full my-auto text-gray-500">
                            <i class="fa-solid fa-circle-notch fa-spin text-4xl text-blue-600 mb-3"></i>
                            <span class="text-sm font-medium">Cargando documento...</span>
                        </div>

                        <!-- Renderizado PDF en Canvas con PDF.js (Scroll 100% nativo y sin menús de Chrome) -->
                        <div
                            v-else-if="isPdf && pdfJsAvailable && !pdfLoadError"
                            class="flex flex-col items-center gap-6 py-4 w-full"
                            @contextmenu.prevent
                        >
                            <div
                                v-for="n in totalPages"
                                :key="'pdf-page-' + n"
                                class="relative flex flex-col items-center bg-white shadow-xl rounded-md overflow-hidden transition-transform duration-150"
                                @contextmenu.prevent
                            >
                                <canvas
                                    :ref="el => { if (el) pdfCanvasRefs[n - 1] = el; }"
                                    class="max-w-none block bg-white"
                                    @contextmenu.prevent
                                ></canvas>
                                
                                <div class="w-full py-1 text-center text-[10px] text-gray-400 bg-gray-50 border-t border-gray-100 select-none">
                                    Página {{ n }} de {{ totalPages }}
                                </div>
                            </div>
                        </div>

                        <!-- Fallback PDF iframe (si PDF.js no se cargó) -->
                        <div
                            v-else-if="isPdf"
                            class="w-full h-full relative rounded-lg overflow-hidden shadow-inner bg-white"
                            @contextmenu.prevent
                        >
                            <iframe
                                :src="fileUrl + '#toolbar=0&navpanes=0'"
                                class="w-full h-full border-0"
                            ></iframe>
                        </div>

                        <!-- Imagen -->
                        <div v-else-if="isImage" class="w-full h-full flex items-center justify-center p-2" @contextmenu.prevent>
                            <img
                                :src="fileUrl"
                                :alt="fileName"
                                class="max-h-full max-w-full object-contain rounded-lg shadow-md transition-transform"
                                :class="{ 'pointer-events-none select-none': onlyread }"
                                @contextmenu.prevent
                                @dragstart.prevent
                            />
                        </div>

                        <!-- Documentos Office u otros con visor embed / fallback -->
                        <div v-else class="w-full h-full flex flex-col items-center justify-center text-center p-6 bg-white dark:bg-slate-900 rounded-xl shadow-sm my-auto">
                            <BaseIcon :path="mdiFileDocumentOutline" :size="64" class="text-blue-500 mb-4 animate-bounce" />
                            <h4 class="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2">
                                {{ fileName }}
                            </h4>
                            <p class="text-sm text-gray-500 dark:text-gray-400 max-w-md mb-6">
                                Este archivo no admite vista previa nativa embebida. Puedes abrirlo directamente o utilizar una extensión de navegador compatible (ej. Edición de Office de Google).
                            </p>
                            <div class="flex items-center gap-3">
                                <a
                                    v-if="!onlyread && fileUrl"
                                    :href="fileUrl"
                                    target="_blank"
                                    class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition shadow-md hover:shadow-lg"
                                >
                                    <BaseIcon :path="mdiDownload" :size="18" /> Abrir / Descargar
                                </a>
                                <iframe
                                    v-else
                                    :src="fileUrl"
                                    class="w-full h-full border-0"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped>
.custom-viewer-scrollbar::-webkit-scrollbar {
    width: 10px;
    height: 10px;
}

.custom-viewer-scrollbar::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
}

.custom-viewer-scrollbar::-webkit-scrollbar-thumb {
    background-color: rgba(100, 116, 139, 0.5);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
}

.custom-viewer-scrollbar::-webkit-scrollbar-thumb:hover {
    background-color: rgba(100, 116, 139, 0.8);
}

@media print {
    :global(body) {
        display: none !important;
    }
}
</style>
